const EXCLUDED = new Set(['changeLog', 'updatedAt', 'createdAt', 'orgId']);

export function appendChangeLog(existingEntity, incomingPayload, changedBy) {
  const base = existingEntity || {};
  const log = Array.isArray(base.changeLog) ? [...base.changeLog] : [];
  const now = new Date().toISOString();
  for (const key of Object.keys(incomingPayload)) {
    if (EXCLUDED.has(key)) continue;
    if (JSON.stringify(base[key]) !== JSON.stringify(incomingPayload[key])) {
      log.push({
        timestamp: now,
        field: key,
        previousValue: base[key] !== undefined ? base[key] : null,
        newValue: incomingPayload[key],
        changedBy,
      });
    }
  }
  return log;
}

export function appendControlChangeLogs(existingControls, incomingControls, changedBy) {
  const existingMap = Object.fromEntries((existingControls || []).map(c => [c.id, c]));
  return incomingControls.map(control => {
    const existing = existingMap[control.id];
    if (!existing) return { ...control, changeLog: [] };
    return { ...control, changeLog: appendChangeLog(existing, control, changedBy) };
  });
}

// ─── §11 rule 34 — Immutability after closure ────────────────────────────────
//
// Terminal states by entity type. Records in these states reject field
// updates from their form save entry; only changeLog appends and the
// explicit Settings reopen flow (rule 35) are allowed.
//
// Risk.status="Closed" and Asset.status="Decommissioned" are soft-delete
// (rule 32), not immutability — excluded by design.
export function isRecordImmutable(record, entityType) {
  if (!record) return false;
  switch (entityType) {
    case 'nonconformity':    return record.status === 'Closed';
    case 'incident':         return record.status === 'Closed';
    case 'audit':            return record.status === 'Completed';
    case 'managementReview': return !!record.approvalDate;
    case 'document':         return record.status === 'Superseded' || record.status === 'Retired';
    default:                 return false;
  }
}

// ─── §11 rule 35 — Prior editable state per entity ───────────────────────────
//
// When a record is reopened from Settings, these are the values it is set
// back to. Drawn from the per-entity status machines in GRC_ARCHITECTURE.md §4:
//   NC               "Open"|"In Progress"|"Closed"     → "In Progress"
//   Incident         "Open"|"Investigating"|"Contained"|"Resolved"|"Closed" → "Resolved"
//   Audit            "Planned"|"In Progress"|"Completed" → "In Progress"
//   ManagementReview approvalDate set → approvalDate null
//   Document         "Draft"|"In Review"|"Approved"|"Superseded"|"Retired"  → "In Review"
//
// For the three entities with a `status` enum the changeLog `field` is
// "status" and the values are enum members. For MR the terminal state is
// not a status value but `approvalDate` being set, so the changeLog entry
// records the actual field that changes (`approvalDate`) with the stored
// date as previousValue and null as newValue.
export function priorEditableStateDescriptor(entityType, record) {
  switch (entityType) {
    case 'nonconformity':
      return {
        field: 'status',
        previousValue: record.status,
        newValue: 'In Progress',
        patch: { status: 'In Progress', closedDate: null },
      };
    case 'incident':
      return {
        field: 'status',
        previousValue: record.status,
        newValue: 'Resolved',
        patch: { status: 'Resolved', closedAt: null },
      };
    case 'audit':
      return {
        field: 'status',
        previousValue: record.status,
        newValue: 'In Progress',
        patch: { status: 'In Progress' },
      };
    case 'managementReview':
      return {
        field: 'approvalDate',
        previousValue: record.approvalDate,
        newValue: null,
        patch: { approvalDate: null },
      };
    case 'document':
      return {
        field: 'status',
        previousValue: record.status,
        newValue: 'In Review',
        patch: { status: 'In Review' },
      };
    default:
      return null;
  }
}
